import { ACTION_TYPE } from '../core/redux/actionType';
import { updateBreaker } from '../core/redux/api/circuitBreaker/module';
import { apiRetryProductHydrationErrors } from '../core/redux/api/endpoints';
import { addError, addLoading, getError, getLoading, removeLoadingAndError } from '../core/redux/api/module';
import { ApiError } from '../core/redux/api/apiError';
import { ApiReducer, makeApiAction, makeApiReducer, RsaaTypes } from '../core/redux/api/reduxStandardApiAction';
import { IAppState } from '../core/redux/IAppState';
import { getRetryRequestIdFromRequest, ProductHydrationErrors, RetryHydrationErrorsRequest } from './hydrationErrors';


const KEY_ACTION = ACTION_TYPE.RETRY_PRODUCT_HYDRATION_ERRORS_REQUEST;
export const ACTIONS    = [
  ACTION_TYPE.RETRY_PRODUCT_HYDRATION_ERRORS_REQUEST,
  ACTION_TYPE.RETRY_PRODUCT_HYDRATION_ERRORS_RESPONSE,
  ACTION_TYPE.RETRY_PRODUCT_HYDRATION_ERRORS_ERROR,
];


/**
 * Whether a retry is in flight for the given request. Keyed by the request, not the action type, so read it
 * through here — the reducer derives its key from the same builder.
 */
export const isRetryHydrationErrorsLoading = (
  state: IAppState,
  request?: RetryHydrationErrorsRequest,
): boolean => !!request && !!getLoading(state, getRetryRequestIdFromRequest(request), KEY_ACTION);


/**
 * The failure, if any, of the retry for the given request. Same keying as isRetryHydrationErrorsLoading.
 */
export const getRetryHydrationErrorsError = (
  state: IAppState,
  request?: RetryHydrationErrorsRequest,
): ApiError | undefined => request
  ? getError(state, getRetryRequestIdFromRequest(request), KEY_ACTION)
  : undefined;


/**
 * Makes an action that can be dispatched to get product hydration errors for the specified resort.
 */
export const retryProductHydrationErrorsAction = (
  retryRequest: RetryHydrationErrorsRequest, 
) => makeApiAction(
  {
    endpoint: apiRetryProductHydrationErrors(retryRequest.resortAbbrev, retryRequest.entitlementTypeId, retryRequest.fulfillmentSeasonId, retryRequest.ageBin),
    method:   'POST',
    headers:  (state: IAppState) => ({
      'content-type':  'application/json',
      'Authorization': `Bearer ${state.auth.bearerToken}`,
    }),
    types:    ACTIONS.map(action => ({ type: action, meta: { retryRequest } })) as RsaaTypes,
    bailout:  (state: IAppState) => false,
  },
);


const reducer: ApiReducer<ProductHydrationErrors[]> = (state, action, error, isLoading, payload): IAppState => {
  const replayRequest = action.meta.retryRequest;

  if (isLoading) {
    return addLoading(state, getRetryRequestIdFromRequest(replayRequest), KEY_ACTION);
  }

  const withBreaker = updateBreaker(state, KEY_ACTION, !!error);

  if (error) {
    return addError(
      withBreaker,
      getRetryRequestIdFromRequest(replayRequest),
      KEY_ACTION,
      error
    );
  };

  const noLoadingOrError = removeLoadingAndError(withBreaker, getRetryRequestIdFromRequest(replayRequest), KEY_ACTION);

  return noLoadingOrError;
};



/**
 * Function that maps actions into new app state. Specifically, updates app state with information from actions related
 * to product hydration errors. Returns a new app state.
 */
export const retryProductHydrationErrorsReducer = makeApiReducer<ProductHydrationErrors[]>(ACTIONS, KEY_ACTION, reducer);
